import { useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'budgie:theme';
// Matches --background in index.css, for the browser/PWA chrome
const CHROME_COLOR = { light: '#f5f4ef', dark: '#111310' } as const;

function readThemePref(): ThemePref {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

const media =
  typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;

function resolveTheme(pref: ThemePref): 'light' | 'dark' {
  return pref === 'system' ? (media?.matches ? 'dark' : 'light') : pref;
}

/** Toggle the .dark class and the browser chrome colour. index.html does the same before first paint. */
function applyTheme(pref: ThemePref) {
  const theme = resolveTheme(pref);
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', CHROME_COLOR[theme]);
}

// One shared preference for every component that shows or changes it
let current: ThemePref = typeof window !== 'undefined' ? readThemePref() : 'system';
const listeners = new Set<() => void>();

media?.addEventListener('change', () => {
  if (current === 'system') {
    applyTheme('system');
    for (const l of listeners) l();
  }
});

export function setThemePref(next: ThemePref) {
  try {
    if (next === 'system') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage unavailable: the choice still applies for this visit
  }
  current = next;
  applyTheme(next);
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The stored preference and the theme actually shown (follows the OS on "system"). */
export function useTheme() {
  const pref = useSyncExternalStore(subscribe, () => current);
  // Re-read on OS changes too, since the snapshot only covers the preference
  const theme = useSyncExternalStore(subscribe, () => resolveTheme(current));
  return { pref, theme, setPref: setThemePref };
}
